import React, { useEffect, useState } from "react";
import { AUTH_PROVIDERS } from "utils/constants";
import { MsalProvider } from "@azure/msal-react";
import { tenantSettings } from "utils/authentication/tenantSettings";
import { AuthProvider, useAuth, hasAuthParams } from "react-oidc-context";
import { AzureApolloConnection } from "./AzureApolloConnection";
import { PublicClientApplication } from "@azure/msal-browser";
import { OptimalApolloConnection } from "./OptimalApolloConnection";
export interface IReactNodeProps {
  children: React.ReactNode;
}

export type TenantAuthProviderProps = IReactNodeProps;
export type OidcWrapperProps = IReactNodeProps;

const OidcWrapper = ({ children }: OidcWrapperProps): React.ReactElement | null => {
  const auth = useAuth();
  useEffect(() => {
    const canRedirect =
      !hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading;
    if (canRedirect) {
      //capture the url user is trying to access?
      window.sessionStorage.setItem("redirectTo", `${location.pathname}${location.search}`);
      auth.signinRedirect({ redirectMethod: "replace" });
    }
  }, [auth]);

  if (auth.error) {
    return <div>Oops... {auth.error.message}</div>;
  }
  if (auth.isAuthenticated) {
    return <>{children}</>;
  }
  return <></>;
};

const onSigninCallback = (): void => {
  window.history.replaceState({}, document.title, window.location.pathname);
  const redirectToUrl = sessionStorage.getItem("redirectTo");
  if (redirectToUrl) {
    sessionStorage.removeItem("redirectTo");
    window.location.href = redirectToUrl;
  }
};

export const TenantAuthProvider: React.FC<
  TenantAuthProviderProps
> = (): React.ReactElement | null => {
  const type = tenantSettings.idp;
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);

  useEffect(() => {
    if (type === AUTH_PROVIDERS.AZURE) {
      const instance = tenantSettings.authService.getConfig();

      // v5 requirement: initialize() must be called and resolved
      // before the instance is passed to the provider.
      instance
        .initialize()
        .then(async () => {
          // CRITICAL: Handle redirect promise after initialization
          // This processes the auth code/token after redirect from Azure AD
          try {
            const response = await instance.handleRedirectPromise();
            if (response) {
              console.log("Successfully handled redirect response");
              // Token acquisition was successful via redirect
            }
          } catch (error) {
            console.error("Error handling redirect:", error);
          }
          setMsalInstance(instance);
        })
        .catch((e) => {
          console.error("MSAL initialization failed:", e);
        });
    }
  }, [type]);

  switch (type) {
    case AUTH_PROVIDERS.AZURE:
      // Don't render the provider until the instance is initialized
      if (!msalInstance) {
        return null; // Or a loading spinner
      }
      return (
        <MsalProvider instance={msalInstance}>
          <AzureApolloConnection />
        </MsalProvider>
      );

    case AUTH_PROVIDERS.OPTIMAL:
      const config = tenantSettings.authService.getConfig();
      config.onSigninCallback = onSigninCallback;
      return (
        <AuthProvider {...config}>
          <OidcWrapper>
            <OptimalApolloConnection />
          </OidcWrapper>
        </AuthProvider>
      );

    default:
      return null;
  }
};
